import React, { useEffect, useState } from "react";
import { api, publicAssetUrl } from "@/lib/api";
import { buildAffiliateUrl } from "@/lib/partnerBrands";
import { trackEvent } from "@/lib/analytics";

const HUB_SLUGS = ["amazon", "flipkart", "myntra", "ajio", "zomato", "swiggy", "blinkit"];

/**
 * Horizontal partner-brand rail — "Also on Offernest".
 * Snapshots the 7 One-Stop Hub brands from `GET /api/partner-brands`.
 * Tap opens the partner site in a new tab with the affiliate URL applied.
 */
export function PartnerBrandsRail() {
    const [brands, setBrands] = useState([]);

    useEffect(() => {
        api.get("/partner-brands").then((r) => {
            const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
            const byslug = Object.fromEntries(items.map((b) => [b.slug, b]));
            const ordered = HUB_SLUGS.map((s) => byslug[s]).filter((b) => b && b.is_active !== false);
            setBrands(ordered);
        }).catch(() => setBrands([]));
    }, []);

    if (brands.length === 0) return null;

    const onTap = (b) => {
        trackEvent("sponsored_ad_click", { source: `partner_brand:${b.slug}`, target_type: "partner_brand", target_id: b.slug });
        const url = buildAffiliateUrl(b);
        if (url && url !== "#") window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <section className="mt-8" data-testid="home-partner-brands-rail">
            <div className="mb-3">
                <h2 className="font-heading text-lg font-bold text-[var(--text-primary)] md:text-xl">Also on Offernest</h2>
                <p className="font-body text-xs text-[var(--text-muted)]">Quick shortcuts to your favourite marketplaces &amp; delivery apps.</p>
            </div>
            <div className="-mx-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-2 md:mx-0 md:grid md:grid-cols-7 md:overflow-visible md:px-0">
                {brands.map((b) => (
                    <button
                        key={b.slug}
                        onClick={() => onTap(b)}
                        data-testid={`partner-brand-${b.slug}`}
                        className="card-soft flex w-24 shrink-0 snap-start flex-col items-center gap-2 !p-3 text-center transition hover:-translate-y-0.5 active:scale-95 md:w-auto"
                        style={{ borderRadius: 20 }}
                    >
                        <div
                            className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl ring-1 ring-slate-100"
                            style={{ background: b.brand_color ? `${b.brand_color}14` : "#FFF3E6" }}
                        >
                            {b.logo_url ? (
                                <img
                                    src={publicAssetUrl(b.logo_url)}
                                    alt={`${b.name} logo`}
                                    className="h-12 w-12 object-contain"
                                    onError={(e) => { e.target.style.display = "none"; }}
                                />
                            ) : (
                                <span className="font-heading text-2xl font-extrabold" style={{ color: b.brand_color || "var(--brand-orange)" }}>
                                    {b.name.charAt(0)}
                                </span>
                            )}
                        </div>
                        <div className="line-clamp-1 font-heading text-xs font-semibold text-[var(--text-primary)]">
                            {b.name}
                        </div>
                    </button>
                ))}
            </div>
        </section>
    );
}

export default PartnerBrandsRail;
